// Service for internal check and data loading
import { useMainStore } from '~/stores/main';
import { saveRedirectLocation } from '~/utils/redirectLocation';
import { TOKEN_COOKIE_OPTIONS } from '~/utils/tokenCookie';

interface Route {
  query?: { token?: string };
  path?: string;
}

interface MSAL {
  isAuthenticated(): boolean;
  hasValidToken?(): boolean;
  data?: {
    user?: {
      idToken?: {
        exp?: number;
        aud?: string;
      };
    };
  };
}

interface Cookies {
  get(key: string): string | null | undefined;
  set(key: string, value: string, options?: any): void;
  remove(key: string): void;
}

interface CheckAndLoadParams {
  route: Route;
  msal?: MSAL;
  cookies: Cookies;
}

class InternalService {
  static async checkAndLoad({ route, msal, cookies }: CheckAndLoadParams): Promise<void> {
    const store = useMainStore();
    let token = '';
    const lang = store.lang;
    let baseUrlApi = store.baseUrlApiGetter;
    if (!baseUrlApi.includes('http')) {
      baseUrlApi = window.location.origin + baseUrlApi;
    }
    // Token logic
    token = route.query?.token || cookies.get('token') || '';
    if (token) {
      cookies.set('token', token, TOKEN_COOKIE_OPTIONS);
      store.hasToken = true;
    } else {
      cookies.remove('token');
      store.hasToken = false;
    }
    // MSAL logic (if present). The cookie outlives the JWT inside it, so an expired
    // token is treated like a missing one and renewed below. The cookie itself stays:
    // it is what tells isAuthenticated() the user had signed in.
    if (token && msal?.hasValidToken?.() === false) {
      token = '';
    }
    if (!token && msal && msal.isAuthenticated()) {
      // Only the absence of a usable token justifies re-authenticating. `store.isInternal`
      // used to be OR-ed in here; it must not be. It is the API's authorization verdict on
      // a token we already hold, so a signed-in user without internal access was pushed to
      // AAD, came straight back (the AAD session is still alive), failed the same test, and
      // bounced forever.
      const expired = msal.hasValidToken ? !msal.hasValidToken() : (msal.data?.user?.idToken?.exp || 0) < Date.now() / 1000;
      if (expired) {
        // Try silent renewal first (uses the cached MSAL account); only fall
        // back to a full interactive redirect if it fails. This recovers
        // internal content on deep pages after the 1h token expiry
        // without a jarring redirect round-trip.
        if (import.meta.client && window.msal && typeof window.msal.silentLogin === 'function') {
          const ok = await window.msal.silentLogin({ scopes: ['openid', 'profile', 'email'] });
          if (ok) {
            token = cookies.get('token') || '';
            if (token) store.hasToken = true;
          }
        }
        if (!token) {
          saveRedirectLocation(route.path || '/', '');
          if (import.meta.client && window.msal) {
            window.msal.loginRedirect({ scopes: ['user.read'] });
          }
          return;
        }
      } else {
        const accessToken = cookies.get('token');
        if (accessToken) {
          token = accessToken;
          store.hasToken = true;
        }
      }
    }
    // Fetch status
    const status = await $fetch(baseUrlApi + 'status/' + lang, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
    if ((status as any).isInternal) {
      store.isInternal = (status as any).isInternal;
      // Fetch categories
      const categories = await $fetch(baseUrlApi + 'channel/' + lang, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      store.categories = categories as any;
      // Optionally, fetch more data as needed (info, downloads, products)
    }
  }
}

export default InternalService;
